/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { InjectionToken } from '@angular/core';
import { getStackedResults } from '@app/sql/helpers/get-stacked-results.helper';
import { mergeReducer } from '@common/utils/merge-reducer';
import { Action, ActionReducer, ActionReducerMap, createReducer, on } from '@ngrx/store';
import { ActionName, ResponseStatus } from '@shared/types/cluster-actions';
import { QueryExportMessage } from '@shared/types/queries';
import { groupBy, uniq } from 'lodash-es';
import { localStorageSync } from 'ngrx-store-localstorage';
import { getSqlMetadataCompositeKey, getSqlMetadataCompositeKeyClusterId } from '../domain/query';
import { QueriesState, State } from '../types';
import {
  cancelExportQuerySuccess,
  cancelQuery,
  cancelQuerySuccess,
  clearQueryResult,
  createQuery3xSuccess,
  executeQuery3xFailure,
  executeQuery3xStarted,
  getNextPageFailure,
  queries,
  queriesLeave,
  query,
  queryActions,
  queryExports,
  removeQueryOk,
  removeResponseItem,
  response,
  responseNextPage,
  responseNextPage3x,
  responseNextPage3xErr,
  sqlMetadata,
  toggleAdvancedSettings,
  toggleParameters,
} from './queries.actions';

export const queriesReducer = createReducer<QueriesState['sqlQueries']>(
  null,
  on(queries, (state, action) => action.list),
  on(query, (state, action) => {
    if (!state) return [action.data];
    const index = state.findIndex((q) => q.id === action.data.id);
    if (index !== -1) return state.map((q, i) => (i === index ? action.data : q));
    else return [...state, action.data];
  }),
  on(removeQueryOk, (state, action) => (state ? state.filter((q) => q.id !== action.id) : state)),
);

export const responsesReducer = createReducer<QueriesState['responses']>(
  new Map(),
  on(response, (state, action) => {
    const response = { ...action.response };
    if (response.status === ResponseStatus.COMPLETED) {
      // TODO: CC-5128 move getStackedResults into appropriate effects
      response.result = getStackedResults(
        response.result.map((r, i) => ({
          ...r,
          // TODO: CC-5128 move this mapping into appropriate effects
          title: action.titles[i],
          type: action.queryType,
          id: `${i}`,
        })),
      );
    }
    return new Map(state.set(action.queryId, response));
  }),
  on(removeResponseItem, (state, action) => {
    const updatedResponse = { ...state.get(action.queryId)! };
    if (updatedResponse.status === ResponseStatus.COMPLETED) {
      updatedResponse.result = updatedResponse.result.filter((r) => r.id !== action.resultId);
    }
    return new Map(state.set(action.queryId, updatedResponse));
  }),
  on(responseNextPage, (state, action) => {
    const updatedResponse = { ...state.get(action.queryId)! };
    if (updatedResponse.status === ResponseStatus.COMPLETED) {
      updatedResponse.result = updatedResponse.result.map((r) =>
        r.cursorId === action.cursorId
          ? { ...r, rows: [...r.rows, ...action.response.rows], hasMore: action.response.hasMore }
          : r,
      );
    }
    return new Map(state.set(action.queryId, updatedResponse));
  }),
  on(getNextPageFailure, (state, action) => {
    const updatedResponse = { ...state.get(action.queryId)! };
    if (updatedResponse.status === ResponseStatus.COMPLETED)
      updatedResponse.result = updatedResponse.result.map((r) =>
        r.cursorId === action.cursorId ? { ...r, failed: true } : r,
      );
    return new Map(state.set(action.queryId, updatedResponse));
  }),
  on(cancelQuerySuccess, clearQueryResult, (state, action) => {
    const newState = new Map(state);
    newState.delete(action.id);
    return newState;
  }),
);
export const runningQueries3xReducer = createReducer<QueriesState['runningQueries3x']>(
  [],
  on(executeQuery3xStarted, (state, action) => uniq([...state, action.queryId])),
  on(createQuery3xSuccess, executeQuery3xFailure, (state, action) => [...state.filter((it) => it !== action.queryId)]),
  on(queriesLeave, () => []),
);

export const queryRuns3xReducer = createReducer<QueriesState['queryRuns3x']>(
  new Map(),
  on(createQuery3xSuccess, (state, action) => new Map([...state, [action.queryId, action.queryRunId]])),
  on(clearQueryResult, removeQueryOk, (state, action) => {
    const newState = new Map(state);
    newState.delete(action.id);
    return newState;
  }),
  on(queriesLeave, () => new Map()),
);

export const cursors3xReducer = createReducer<QueriesState['cursors3x']>(
  new Map(),
  on(createQuery3xSuccess, (state, action) => new Map([...state, [action.queryRunId, action.response]])),
  on(clearQueryResult, removeQueryOk, (state, action) => {
    const newState = new Map(state);
    newState.delete(action.id);
    return newState;
  }),
  on(queriesLeave, () => new Map()),
  on(removeResponseItem, (state, action) => {
    const newState = new Map(state);
    const queryRunId = Array.from(newState.entries()).find(([queryRunId, cursors]) =>
      cursors.some((c) => c.id === action.resultId),
    )?.[0];
    if (!queryRunId) return state;
    const newCursors = newState.get(queryRunId)?.filter((c) => c.id !== action.resultId);
    if (!newCursors) return state;
    newState.set(queryRunId, newCursors);
    return newState;
  }),
);

export const cursorPages3xReducer = createReducer<QueriesState['cursorPages3x']>(
  new Map(),
  on(responseNextPage3x, responseNextPage3xErr, (state, action) => {
    const { cursorId } = action;
    const newCursorPages = [...(state.get(cursorId) ?? []), action.response];
    const newState = new Map(state);
    newState.set(cursorId, newCursorPages);
    return newState;
  }),
  on(clearQueryResult, removeQueryOk, (state, action) => {
    const newState = new Map(state);
    newState.delete(action.id);
    return newState;
  }),
  on(queriesLeave, () => new Map()),
  on(removeResponseItem, (state, action) => {
    const newState = new Map(state);
    newState.delete(action.resultId);
    return state;
  }),
);

export const actionsReducer = createReducer<QueriesState['actions']>(
  {},
  on(queryActions, (state, action) => {
    state = { ...state, [action.status]: [...(state[action.status] || []), ...action.actions] };
    if (action.status !== ResponseStatus.RUNNING) {
      state[ResponseStatus.RUNNING] = (state[ResponseStatus.RUNNING] || []).filter(
        (s) => !action.actions.some((a) => a.id === s.id),
      );
    }
    return state;
  }),
  on(cancelQuerySuccess, (state, { id }) => {
    const action = (state[ResponseStatus.RUNNING] || []).find(
      (s) => s.action === ActionName.SQL_QUERY_EXECUTE && s.argument.queryId === id,
    );
    if (!action) return state;
    return {
      ...state,
      [ResponseStatus.RUNNING]: (state[ResponseStatus.RUNNING] || []).filter((s) => s !== action),
    };
  }),
  on(queriesLeave, () => ({})),
);

export const exportsReducer = createReducer<QueriesState['exports']>(
  {},
  on(queryExports, (state, { exports }) => {
    let runningExports: QueryExportMessage[];
    if (Array.isArray(exports)) {
      runningExports = exports;
    } else if (exports.status === ResponseStatus.RUNNING) {
      runningExports = [...(state[ResponseStatus.RUNNING] || []), exports];
    } else {
      runningExports = (state[ResponseStatus.RUNNING] || []).filter((e) => e.id !== exports.id);
    }
    return {
      ...state,
      [ResponseStatus.RUNNING]: runningExports,
    };
  }),
  on(cancelExportQuerySuccess, (state, { message }) => {
    return {
      ...state,
      [ResponseStatus.CANCELED]: (state[ResponseStatus.CANCELED] || []).filter((it) => it.id !== message.id),
    };
  }),
  on(cancelQuery, (state, { id }) => {
    const runningExport = (state[ResponseStatus.RUNNING] || []).find((msg) => msg.id === id);
    if (!runningExport) return state;
    return {
      ...state,
      [ResponseStatus.CANCELED]: [...(state[ResponseStatus.CANCELED] || []), runningExport],
    };
  }),
  on(queriesLeave, () => ({})),
);

export const sqlMetadataReducer = createReducer<QueriesState['sqlMetadata']>(
  new Map(),
  on(sqlMetadata, (state, action) => {
    return new Map([
      ...Array.from(state).filter(([key]) => getSqlMetadataCompositeKeyClusterId(key) !== action.clusterId),
      ...Object.entries(groupBy(action.data, 'cacheName')).map(
        ([cacheName, data]) => [getSqlMetadataCompositeKey(action.clusterId, cacheName), data] as const,
      ),
    ]);
  }),
  on(queriesLeave, () => new Map()),
);

export const showSQLSidePanelReducer = createReducer<QueriesState['showSQLSidePanel']>(
  { advancedSettings: false, parameters: false },
  on(toggleAdvancedSettings, (state) => ({ advancedSettings: !state.advancedSettings, parameters: false })),
  on(toggleParameters, (state) => ({ advancedSettings: false, parameters: !state.parameters })),
);

export function queriesLocalStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return localStorageSync({
    keys: ['showSQLSidePanel'],
    rehydrate: true,
    removeOnUndefined: true,
    mergeReducer,
  })(reducer);
}

export function makeQueriesInitialState(): QueriesState {
  return {
    sqlQueries: null,
    responses: new Map(),
    runningQueries3x: [],
    queryRuns3x: new Map(),
    cursors3x: new Map(),
    cursorPages3x: new Map(),
    actions: {},
    sqlMetadata: new Map(),
    showSQLSidePanel: { advancedSettings: false, parameters: false },
    exports: {},
  };
}

export const sqlReducers = {
  sqlQueries: queriesReducer,
  responses: responsesReducer,
  runningQueries3x: runningQueries3xReducer,
  queryRuns3x: queryRuns3xReducer,
  cursors3x: cursors3xReducer,
  cursorPages3x: cursorPages3xReducer,
  actions: actionsReducer,
  sqlMetadata: sqlMetadataReducer,
  showSQLSidePanel: showSQLSidePanelReducer,
  exports: exportsReducer,
} as ActionReducerMap<State['queries'], Action>;

export const SQL_REDUCERS = new InjectionToken<ActionReducerMap<State['queries'], Action>>('SQL reducers');
